import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { withTimeout } from '@/lib/queryTimeout';
import type { Product } from '@/lib/types';

const DEFAULT_PAGE_SIZE = 24;

interface UseProductsOptions {
  search?: string;
  categoryId?: string | null;
  activeOnly?: boolean;
  catalogOnly?: boolean;
  pageSize?: number;
  branchId?: string | null;
}

function sanitizeTerm(term: string): string {
  return term.replace(/[%_]/g, '');
}

async function mergeStock(rows: Product[], branchId: string | null): Promise<Product[]> {
  if (rows.length === 0) return rows;
  const ids = rows.map((p) => p.id);
  let stockMap = new Map<string, number>();

  try {
    if (branchId) {
      const { data, error } = await withTimeout(
        supabase
          .from('branch_inventory')
          .select('product_id, stock')
          .eq('branch_id', branchId)
          .in('product_id', ids),
        20000
      );
      if (error) throw error;
      const inv = (data ?? []) as { product_id: string; stock: number }[];
      for (const r of inv) stockMap.set(r.product_id, Number(r.stock ?? 0));
    } else {
      const { data, error } = await withTimeout(
        supabase
          .from('branch_inventory')
          .select('product_id, stock')
          .in('product_id', ids),
        20000
      );
      if (error) throw error;
      const map = new Map<string, number>();
      for (const r of (data ?? []) as { product_id: string; stock: number }[]) {
        map.set(r.product_id, (map.get(r.product_id) ?? 0) + Number(r.stock ?? 0));
      }
      stockMap = map;
    }
  } catch {
    // Si el stock por sucursal falla (red, permisos, etc.), no perdemos los
    // productos: usamos el stock global del producto como respaldo.
    stockMap = new Map(rows.map((p) => [p.id, Number(p.stock ?? 0)]));
  }

  return rows.map((p) => ({ ...p, stock: stockMap.get(p.id) ?? 0 }));
}

export function useProducts(options: UseProductsOptions = {}) {
  const {
    search = '',
    categoryId = null,
    activeOnly = false,
    catalogOnly = false,
    pageSize = DEFAULT_PAGE_SIZE,
    branchId = null,
  } = options;

  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(0);

  const runQuery = useCallback(
    async (reset: boolean) => {
      if (reset) {
        setLoading(true);
        pageRef.current = 0;
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const from = pageRef.current * pageSize;
      const to = from + pageSize - 1;

      try {
        let query = supabase
          .from('products')
          .select('*, category:categories(id, name)', { count: 'exact' });

        if (catalogOnly) {
          query = query.eq('show_in_catalog', true).eq('active', true);
        } else if (activeOnly) {
          query = query.eq('active', true);
        }

        const term = sanitizeTerm(search.trim());
        if (term) {
          query = query.or(
            `name.ilike.%${term}%,sku.ilike.%${term}%,barcode.ilike.%${term}%,brand.ilike.%${term}%`
          );
        }

        if (categoryId) {
          query = query.eq('category_id', categoryId);
        }

        const { data, count, error: e } = await withTimeout(
          query.range(from, to).order('name', { ascending: true }),
          20000
        );

        if (e) throw e;

        const rows = (data as Product[]) ?? [];
        const withStock = await mergeStock(rows, branchId);

        setTotal(count ?? 0);
        setProducts((prev) => (reset ? withStock : [...prev, ...withStock]));
        pageRef.current += 1;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudieron cargar los productos');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize, search, categoryId, activeOnly, catalogOnly, branchId]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      runQuery(true);
    }, 300);
    return () => clearTimeout(t);
  }, [runQuery]);

  const loadMore = useCallback(() => runQuery(false), [runQuery]);
  const reload = useCallback(() => runQuery(true), [runQuery]);
  const hasMore = products.length < total;

  return { products, total, loading, loadingMore, error, hasMore, loadMore, reload };
}